import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Instagram, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';

// Mock Instagram posts
const instagramPosts = [
  'https://images.unsplash.com/photo-1610030469983-98e550d6193c?w=400&q=80',
  'https://images.unsplash.com/photo-1594463750939-ebb28c3f7f75?w=400&q=80',
  'https://images.unsplash.com/photo-1515562141207-7a88fb7ce338?w=400&q=80',
  'https://images.unsplash.com/photo-1549465220-1a8b9238cd48?w=400&q=80',
  'https://images.unsplash.com/photo-1583391733956-3750e0ff4e8b?w=400&q=80',
  'https://images.unsplash.com/photo-1607344645866-009c320b63e0?w=400&q=80',
];

const InstagramSection = () => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: '-100px' });

  return (
    <section ref={sectionRef} className="py-16 md:py-24 bg-background">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-10"
        >
          <div className="inline-flex items-center gap-2 mb-4">
            <Instagram className="h-5 w-5 text-silver" />
            <span className="text-silver font-sans">@fazila_creations</span>
          </div>
          <h2 className="font-display text-2xl md:text-3xl text-foreground mb-4">
            Follow Our Journey
          </h2>
          <p className="text-muted-foreground font-body max-w-md mx-auto">
            See our latest creations and behind-the-scenes moments
          </p>
        </motion.div>

        {/* Instagram Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {instagramPosts.map((post, index) => (
            <motion.a
              key={index}
              href="https://instagram.com/fazila_creations"
              target="_blank"
              rel="noopener noreferrer"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={isInView ? { opacity: 1, scale: 1 } : {}}
              transition={{ duration: 0.4, delay: index * 0.05 }}
              className="group relative aspect-square rounded-xl overflow-hidden border border-silver/10"
            >
              <img
                src={post}
                alt={`Instagram post ${index + 1}`}
                className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-primary/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                <ExternalLink className="h-6 w-6 text-ivory" />
              </div>
            </motion.a>
          ))}
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="text-center mt-8"
        >
          <a
            href="https://instagram.com/fazila_creations"
            target="_blank"
            rel="noopener noreferrer"
          >
            <Button variant="outline" className="group">
              <Instagram className="h-4 w-4" />
              Follow on Instagram
              <ExternalLink className="h-3 w-3 opacity-50 group-hover:opacity-100 transition-opacity" />
            </Button>
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default InstagramSection;
